/**
 * The settings panels, as data — the same treatment the module sections get.
 *
 * S-D3 drew eight. **All eight are built as of S-G1**, and the history of the
 * four that were not is kept because it is the record of what each one was
 * waiting for — a panel listed here disabled was always waiting on a backend,
 * never on somebody getting round to it:
 *
 *  * ~~**Connections**~~ — **built in S-B17**, once there was something to
 *    connect to: GSC (S-B22), LinkedIn (S-B17), sender identity (S-B18).
 *  * ~~**Brand voice**~~ — **built in S-G1.** The wizard has written this row
 *    since S-B9 and `app/seo/content.py` has read it since S-B25; what was
 *    missing was a way to change it once the wizard was behind you, which
 *    S-D2's own step-6 copy promises. It edits the same singleton row over the
 *    wizard's own endpoints rather than a second pair of routes.
 *  * ~~**Budget caps**~~ — **built in S-B7.** The ledger it needed exists: a
 *    ceiling per tenant, checked before every paid call, with the one-off raise
 *    `admin#overage` offers. Visible to every member and editable by owners and
 *    admins, which is what S-D3's role matrix draws.
 *  * ~~**Notifications**~~ — **built in S-B10.** The concern S-B8 recorded here
 *    was that a preferences table keyed on events no code emits would be a
 *    schema written against a guess; the answer was to take the vocabulary from
 *    S-D3's own table — five approved rows — and record, per event, which
 *    session emits it. Three are emitted today and the rest render with a
 *    "from S-Bxx" note, the same treatment the sidebar gives an unshipped
 *    module. The panel also carries the webhook endpoints and their delivery
 *    log, because S-D3 designed no panel for those and adding a ninth entry to
 *    approved navigation would be inventing information architecture.
 *  * ~~**Danger zone**~~ — **built in S-B12.** DPDP export and organisation
 *    deletion are §8 launch requirements that session owns, and both are live.
 *    Pause-all-automation is still there and still disabled, because there is
 *    genuinely no automation to pause until the SEO and outreach runners land —
 *    rendered rather than removed, so the approved panel keeps its shape.
 *
 * The convention, kept for whatever S-D3 draws next: a panel with no backend is
 * shown disabled rather than hidden, exactly as the sidebar sections are, so the
 * settings information architecture is visible and none of it is a stub. What
 * S-G1 found is the failure mode that convention has — `arrivesIn` naming a
 * session that closed weeks earlier, with nobody looking at it again — so
 * `panels.test.ts` now asserts that every disabled entry names a session that is
 * genuinely still open.
 */

export interface SettingsPanel {
  label: string
  to?: string
  /** Permission required to *see* it — mirrors the route dependency on the API. */
  permission?: string
  arrivesIn: string
}

export const SETTINGS_PANELS: readonly SettingsPanel[] = [
  {
    label: 'Organisation',
    to: '/settings/organisation',
    permission: 'tenant:read',
    arrivesIn: 'S-B8',
  },
  { label: 'Team & roles', to: '/settings/team', permission: 'members:read', arrivesIn: 'S-B8' },
  {
    // S-B17. **Back to S-D3's eight.** S-B14 added a ninth entry — *Browser
    // extension* — because the panel it belonged in did not exist and an
    // extension nobody could pair was an extension nobody could use. That panel
    // exists now, the extension is a card inside it, and the ninth entry is gone
    // exactly as S-B14 said it would be. `/settings/extension` redirects here.
    //
    // `tenant:read` rather than `discovery:manage`: a member who cannot connect
    // LinkedIn still needs to see whether it is connected, because that is why
    // their outreach is or is not going out. The card's own controls are
    // refused by the API, which is the boundary that actually holds.
    label: 'Connections',
    to: '/settings/connections',
    permission: 'tenant:read',
    arrivesIn: 'S-B17',
  },
  {
    // S-G1. **Was the drift.** This read `{ label: 'Brand voice', arrivesIn:
    // 'S-B25' }` — disabled, naming a session that closed on 2026-08-11 — long
    // after the row it edits was being written by the wizard and read by
    // content generation. `tenant:read` for the same reason Connections uses
    // it: every role needs to see the voice their drafts are written in, and
    // the API refuses a member's write regardless.
    label: 'Brand voice',
    to: '/settings/brand-voice',
    permission: 'tenant:read',
    arrivesIn: 'S-B25',
  },
  {
    label: 'Budget caps',
    to: '/settings/budget',
    permission: 'tenant:read',
    arrivesIn: 'S-B7',
  },
  {
    label: 'Notifications',
    to: '/settings/notifications',
    permission: 'tenant:read',
    arrivesIn: 'S-B10',
  },
  { label: 'Active sessions', to: '/settings/sessions', arrivesIn: 'S-B8' },
  {
    label: 'Danger zone',
    to: '/settings/danger-zone',
    permission: 'data:export',
    arrivesIn: 'S-B12',
  },
]

export function panelForPath(pathname: string): SettingsPanel | undefined {
  return SETTINGS_PANELS.find((panel) => panel.to === pathname)
}
